/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2022 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import type { RawDraftContentBlock } from "draft-js";
import type { Descendant } from "slate";
import type { Socket } from "socket.io";

import type { Activity, Block as PrismaBlock, Page as PrismaPage, User as PrismaUser } from "@vienna/prisma";

import { BlockSnapshot } from "..";
import { TransactionType } from "../enums";

export interface NextFunction {
  (err?: any): void;
  (deferToNext: "route"): void;
}

export interface ParamsDictionary {
  [key: string]: string;
}

/**
 * Helper to make inspected types easier to understand
 */
export type Identity<T> = {
  [K in keyof T]: T[K];
};

/**
 * Optionalize the keys K of in type T
 */
export type Optionalize<T, K extends keyof T> = Identity<Omit<T, K> & Partial<T>>;

/**
 * generic that is a partial aside from the a passed in key
 * usage: AtLeast<Block, "id"> is equivalent to Partial<Block> & { id: string }
 */
export type AtLeast<T, K extends keyof T> = Partial<T> & Pick<T, K>;

/**
 * make optional properties that satisfy V
 */
export type OptionalizeNulls<T, V> = Pick<T, { [K in keyof T]-?: T[K] extends V ? never : K }[keyof T]>;

export type ModelOnlyCols = "id" | "createdAt" | "updatedAt";

export interface StyleLabel {
  type: string;
  markupId?: string;
}

/* -------------------------------------------------- */
/*                   Activity Types                   */
/* -------------------------------------------------- */

export interface ViennaActivity extends Activity {
  user: Partial<PrismaUser> & { followers?: Partial<PrismaUser>[] };
  userFollowed: Partial<PrismaUser>;
  block: PrismaBlock & {
    numberOfEmbeds?: number;
    embeddedBlock: PrismaBlock & { numberOfEmbeds?: number };
    page: PrismaPage;
    user: Partial<PrismaUser>;
  };
  page: PrismaPage;
}

/* ------------------ Electron Update Types ------------------ */

/**
 * The type of data returned from electron autoUpdater
 * about the progress of the new version download
 */
export type UpdateElectronInfo = {
  /** number of bits downloaded of update */
  total: number;
  /** delta bits from last check in */
  delta: number;
  /** transferred bits */
  transferred: number;
  /** percent */
  percent: number;
  /** bytesPerSecond */
  bytesPerSecond: number;
};

/**
 * Returned from autoUpdater listeners
 */
export type UpdateState =
  | "error"
  | "checking-for-update"
  | "update-available"
  | "update-not-available"
  | "update-downloaded"
  | "download-progress";

/**
 * The update state of the application
 */
export type UpdateStatus = {
  /** the check update status */
  updateState: UpdateState;
  /** download progress */
  download?: UpdateElectronInfo;
};

export type SlateDescendent = Descendant;
export type DraftType = RawDraftContentBlock;

export type MouseBeacon = { username: string; mousePosition: { x: number; y: number } };

/* -------------------------------------------------- */
/*                    Socket Types                    */
/* -------------------------------------------------- */

/* ------------------ Socket.io Client Types ------------------ */

export type CreateTransaction = BaseTransaction & {
  type: TransactionType.Create;
  blocks: BlockSnapshot[];
};

export type UpdateTransaction = BaseTransaction & {
  type: TransactionType.Update;
  blocks: AtLeast<BlockSnapshot, "id">[];
};

export type DeleteTransaction = BaseTransaction & {
  type: TransactionType.Delete;
  blockIds: string[];
};

type BaseTransaction = {
  hash: string;
  size: number;
  time: Date;
  plateId: string;
  userId: string;
};

export type Transaction = CreateTransaction | UpdateTransaction | DeleteTransaction;

type SocketHandshake = {
  auth: {
    token?: string;
    role?: "plate" | "urbit" | "n/f";
    urbitRole?: "signup" | "signin";
    pageId?: string;
  };
};

export type SocketUser = { username: string; avatarUrl: string | null; registeredWithUrbit: boolean };

export type AugmentedSocket = Socket & {
  currentUser?: PrismaUser;
  token?: string;
  role?: string;
  pageId?: string;
  userShipName?: string;
  urbitCookie?: string;
  urbitSignup?: boolean;
  urbitRole?: "signup" | "signin";
  handshake: SocketHandshake;
};

/* ------------------ Socket Event Types ------------------ */

export enum ServerSocketEvents {
  // plate
  UserJoined = "USER_JOINED",
  UserLeft = "USER_LEFT",
  MouseBeacon = "MOUSE_BEACON",
  PresentUsers = "PRESENT_USERS",
  LockedBlocks = "LOCKED_BLOCKS",
  UnlockedBlocks = "UNLOCKED_BLOCKS",
  PlateUpdate = "PLATE_UPDATE",
  PageSavedReceipt = "PAGE_SAVED_RECEIPT",
  // notification
  Notification = "NOTIFICATION_ACTIVITY_POSTED",
}

export enum NotificationEvents {
  FOLLOW = "FOLLOW",
  PAGE_SHARED = "PAGE_SHARED",
  BLOCK_OR_PAGE_LIKE = "BLOCK_OR_PAGE_LIKE",
  ADMIN_ANNOUNCEMENT = "ADMIN_ANNOUNCEMENT",
}

/**
 * Map of the server to client socket event enum to the payload data
 */
export interface ServerToClientPayloads {
  [ServerSocketEvents.UserJoined]: (user: SocketUser) => void;
  [ServerSocketEvents.UserLeft]: (username: string) => void;
  [ServerSocketEvents.MouseBeacon]: (beacon: MouseBeacon) => void;
  [ServerSocketEvents.PresentUsers]: (users: SocketUser[]) => void;
  [ServerSocketEvents.LockedBlocks]: (data: { lockedBlocks: string[]; username: string }) => void;
  [ServerSocketEvents.UnlockedBlocks]: (data: { unlockedBlocks: string[]; username: string }) => void;
  [ServerSocketEvents.PlateUpdate]: (data: Transaction) => void;
  [ServerSocketEvents.PageSavedReceipt]: (handledChangeHash: string) => void;
}

export enum ClientSocketEvents {
  WhoIsHere = "who-is-here?",
  LockBlocks = "lock-blocks",
  UnlockBlocks = "unlock-blocks",
  MouseBeacon = "mouse-beacon",
  PlateUpdate = "plate-update",
}

/**
 * Map of the client to server socket event enum to the payload data
 */

export interface ClientToServerPayloads {
  [ClientSocketEvents.LockBlocks]: (data: { lockedBlocks: string[] }) => void;
  [ClientSocketEvents.UnlockBlocks]: (data: { unlockedBlocks: string[] }) => void;
  [ClientSocketEvents.MouseBeacon]: (beacon: MouseBeacon) => void;
  [ClientSocketEvents.PlateUpdate]: (data: Transaction) => void;
  [ClientSocketEvents.WhoIsHere]: () => void;
}

export enum NativeSocketEvents {
  Connect = "connect",
  Disconnect = "disconnect",
  ConnectError = "connect_error",
}

export type AuthResponse = {
  token: string;
  user: Omit<PrismaUser, "following" | "followers" | "likes" | "pages" | "settings">;
  urbitPrivateKey?: string;
};

export enum UrbitSocketClientEvents {
  ShipName = "ship-name",
}

export enum UrbitSocketServerEvents {
  LogMessage = "log-message",
  AuthApprovalUpdate = "auth-approval-update",
}

export interface FileHeaders {
  "content-type"?: string;
  "content-length"?: string;
  "content-disposition"?: string;
  [header: string]: string | undefined; // for any other headers
}
